function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** architect_will_coach, falling back to the legacy master_will_coach alias until BE-4. */
export function readArchitectWillCoach(row = {}) {
  return row.architect_will_coach ?? row.master_will_coach;
}

/** architect_will_coach_holders, falling back to the legacy master_will_coach_holders alias until BE-4. */
export function readArchitectWillCoachHolders(row = {}) {
  return asArray(row.architect_will_coach_holders ?? row.master_will_coach_holders);
}

/** architects, falling back to the legacy masters alias until BE-4. */
export function readArchitects(row = {}) {
  return row.architects ?? row.masters;
}

function personAvatarUrl(person = {}) {
  return person.avatar || person.img || person.picture || null;
}

function personNameParts(person = {}) {
  const firstName = String(person.firstName ?? person.first_name ?? "").trim();
  const lastName = String(person.lastName ?? person.last_name ?? "").trim();
  if (firstName || lastName) return { firstName, lastName };

  const full = String(person.full_name ?? person.name ?? "").trim();
  if (!full) return { firstName: "", lastName: "" };
  const parts = full.split(/\s+/);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "" };
}

function coachPersonDisplayName(person = {}) {
  const { firstName, lastName } = personNameParts(person);
  const composed = `${firstName} ${lastName}`.trim();
  return (composed || String(person.name ?? "").trim()).toLowerCase();
}

function normalizedPersonName(person = {}) {
  const direct = String(person.name ?? person.full_name ?? "")
    .trim()
    .toLowerCase();
  if (direct) return direct;
  return coachPersonDisplayName(person);
}

function isSameCoachPerson(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && a.id === b.id) return true;
  if (a.employee_id != null && b.id != null && a.employee_id === b.id) return true;
  if (a.id != null && b.employee_id != null && a.id === b.employee_id) return true;
  if (a.employee_id != null && b.employee_id != null && a.employee_id === b.employee_id) {
    return true;
  }
  const nameA = normalizedPersonName(a);
  const nameB = normalizedPersonName(b);
  return Boolean(nameA && nameB && nameA === nameB);
}

function mergeCoachPersonRecords(existing, incoming) {
  const existingHasStructuredName = Boolean(
    existing.first_name || existing.firstName || existing.last_name || existing.lastName
  );
  const incomingHasStructuredName = Boolean(
    incoming.first_name || incoming.firstName || incoming.last_name || incoming.lastName
  );
  const preferred = incomingHasStructuredName && !existingHasStructuredName ? incoming : existing;
  const other = preferred === incoming ? existing : incoming;
  return {
    ...other,
    ...preferred,
    avatar: personAvatarUrl(existing) || personAvatarUrl(incoming),
  };
}

/** Build API-shaped coach entry for the signed-in user. */
export function coachPersonFromUser(user) {
  if (!user) return null;
  const { firstName, lastName } = personNameParts(user);
  const name = `${firstName} ${lastName}`.trim() || String(user.name ?? "").trim();
  const id = user.id ?? user.employee_id;
  if (id == null) return null;
  return { id, name, avatar: personAvatarUrl(user) };
}

export function coachAvatarFromPerson(person = {}) {
  const { firstName, lastName } = personNameParts(person);
  return { img: personAvatarUrl(person), firstName, lastName };
}

export function badgeHolderToAvatar(holder = {}) {
  return coachAvatarFromPerson(holder);
}

/** Merge coach people lists without duplicates (by id or name). */
export function mergeCoachPeopleLists(...lists) {
  const merged = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const person of list) {
      const idx = merged.findIndex((existing) => isSameCoachPerson(existing, person));
      if (idx < 0) merged.push(person);
      else merged[idx] = mergeCoachPersonRecords(merged[idx], person);
    }
  }
  return merged;
}

/** Remove coaches who are already listed as architects (badge-holder fallback only). */
export function excludePeopleInArchitectsList(coaches, architects) {
  if (!Array.isArray(coaches) || coaches.length === 0) return [];
  if (!Array.isArray(architects) || architects.length === 0) return coaches;
  return coaches.filter(
    (coach) => !architects.some((architect) => isSameCoachPerson(coach, architect))
  );
}

/** Merge coaches + architect_will_coach_holders when Coach too? is on; coaches only otherwise. */
export function resolveCoachesPeopleForRow(row = {}) {
  const willCoach = readArchitectWillCoach(row) === true;
  if (willCoach) {
    return mergeCoachPeopleLists(asArray(row.coaches), readArchitectWillCoachHolders(row));
  }
  return mergeCoachPeopleLists(asArray(row.coaches));
}

/**
 * Coaches column source list for an architected-responsibility row.
 * When architect_will_coach is true, merges coaches with architect_will_coach_holders (deduped).
 * When false, uses API coaches only.
 */
export function resolveOriginalCoachesForRow(row = {}, authUser = null) {
  let list = resolveCoachesPeopleForRow(row);
  if (readArchitectWillCoach(row) === true && authUser && list.length === 0) {
    list = syncOriginalCoachesForArchitectWillCoach(list, true, authUser);
  }
  return list;
}

/** Add or remove the current user from the raw coaches list after Coach too? toggle. */
export function syncOriginalCoachesForArchitectWillCoach(
  originalCoaches,
  architectWillCoach,
  user
) {
  const person = coachPersonFromUser(user);
  const list = Array.isArray(originalCoaches) ? [...originalCoaches] : [];
  if (!person) return list;
  const idx = list.findIndex((c) => isSameCoachPerson(c, person));
  if (architectWillCoach === true) {
    if (idx < 0) list.push(person);
    return list;
  }
  return idx >= 0 ? list.filter((_, i) => i !== idx) : list;
}

/** Map people to avatar cells; dedupe by id or name and prefer photo over initials. */
export function mapCoachPeopleToAvatars(people) {
  if (!Array.isArray(people) || people.length === 0) return [];
  return mergeCoachPeopleLists(people).map((person) => coachAvatarFromPerson(person));
}

/** Coaches to render from resolved people list, or merge API sources when needed. */
export function getCoachesForDisplay(row = {}) {
  if (readArchitectWillCoach(row) === true) {
    const resolved = asArray(row.coaches);
    if (resolved.length > 0) {
      return mergeCoachPeopleLists(resolved);
    }
    return mergeCoachPeopleLists(asArray(row.rawCoaches), readArchitectWillCoachHolders(row));
  }
  return mergeCoachPeopleLists(asArray(row.rawCoaches), asArray(row.coaches));
}

/** @deprecated Use mapCoachPeopleToAvatars */
export function dedupeCoachAvatars(avatars = []) {
  return mapCoachPeopleToAvatars(
    avatars.map((avatar) => ({
      firstName: avatar.firstName,
      lastName: avatar.lastName,
      avatar: avatar.img,
    }))
  );
}

export function coachHoldersFromBadgeHolders(row = {}) {
  const coachesOnly = asArray(row.badge_holders).filter(
    (bh) => String(bh.badge_level || "").toLowerCase() === "coach"
  );
  return coachesOnly.length > 0 ? coachesOnly : null;
}

/** Build coaches column avatars from API coaches list and badge-holder fallbacks. */
export function buildCoachesAvatarsForRow(row = {}) {
  const coaches = getCoachesForDisplay(row);
  if (coaches.length > 0) return mapCoachPeopleToAvatars(coaches);
  if (asArray(row.rawCoaches).length > 0) return [];

  for (const list of [row.coach_badge_holders, coachHoldersFromBadgeHolders(row)]) {
    if (!Array.isArray(list) || list.length === 0) continue;
    const filtered = excludePeopleInArchitectsList(list, readArchitects(row));
    if (filtered.length > 0) return mapCoachPeopleToAvatars(filtered);
  }
  return [];
}

/** Apply architect_will_coach toggle to an architected-responsibility table row. */
export function patchArchitectWillCoachRow(
  row,
  architectWillCoach,
  updatedResponsibility,
  authUser
) {
  const willCoach = architectWillCoach === true;
  const patchedRow = {
    ...row,
    architect_will_coach: willCoach,
    architect_will_coach_holders:
      updatedResponsibility?.architect_will_coach_holders ??
      updatedResponsibility?.master_will_coach_holders ??
      row.architect_will_coach_holders ??
      row.master_will_coach_holders,
    coaches: updatedResponsibility?.coaches ?? row.coaches,
    rawCoaches: updatedResponsibility?.coaches ?? row.rawCoaches ?? row.coaches,
  };
  const originalCoaches = resolveOriginalCoachesForRow(patchedRow, authUser);

  const avatars = buildCoachesAvatarsForRow({
    ...patchedRow,
    coaches: originalCoaches,
  });

  return {
    ...patchedRow,
    isCoach: willCoach,
    originalCoaches,
    coaches: {
      avatars,
      maxVisible: row.coaches?.maxVisible ?? 3,
    },
  };
}
